import { useState, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SlidersHorizontal, X } from "lucide-react";
import { cn } from "@/lib/utils";

export interface FilterColumn {
  id: string;
  label: string;
  options: { value: string; label: string; imageUrl?: string }[];
  /**
   * AUT-478: how many grid columns this filter occupies in `CombinedFilterDropdown`'s checkbox
   * panel. Defaults to 1. The prototype carries the same idea as a `fullWidth` flag that spans two
   * columns — used here so a long option label fits on one line, as it does in the design.
   * Ignored by `FilterDropdown`, which is a single-column list.
   */
  columnSpan?: number;
  /**
   * How many columns this filter's option list uses inside `FilterDropdown`. Defaults to 1 (the
   * single list every caller has today). 2 lays the options out in two columns, gives the filter's
   * own column a fixed, bounded width of `w-[min(36rem,80vw)]`, and caps the list at `max-h-[min(24rem,60vh)]` with a scroll (human ruling
   * 2026-10-13: a category list can run to 200+ entries). Ignored by `CombinedFilterDropdown`.
   */
  optionColumns?: 1 | 2;
}

interface FilterDropdownProps {
  columns: FilterColumn[];
  activeFilters: Record<string, string[]>;
  onFiltersChange: (filters: Record<string, string[]>) => void;
  className?: string;
  /** AUT-525: pin the panel BELOW the trigger (never flip up) and cap its height with an inner
   * scroll. Opt-in so every other screen keeps Radix's default collision flipping. Used by the rank
   * tracker, where an expanded Signal AI Rank Report pushes the Filters button far enough down the
   * page that Radix flips the panel up and clips its top. */
  pinBelow?: boolean;
}

export function FilterDropdown({ columns, activeFilters, onFiltersChange, className, pinBelow = false }: FilterDropdownProps) {
  const [open, setOpen] = useState(false);

  const activeCount = Object.values(activeFilters).reduce((sum, arr) => sum + arr.length, 0);

  const toggleValue = (columnId: string, value: string) => {
    const current = activeFilters[columnId] || [];
    const next = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    onFiltersChange({ ...activeFilters, [columnId]: next });
  };

  const clearAll = () => {
    const cleared: Record<string, string[]> = {};
    columns.forEach(col => { cleared[col.id] = []; });
    onFiltersChange(cleared);
  };

  const removeFilter = (columnId: string, value: string) => {
    const current = activeFilters[columnId] || [];
    onFiltersChange({ ...activeFilters, [columnId]: current.filter(v => v !== value) });
  };

  // Build chips from active filters
  const chips: { columnId: string; columnLabel: string; value: string; label: string }[] = [];
  columns.forEach(col => {
    (activeFilters[col.id] || []).forEach(val => {
      const opt = col.options.find(o => o.value === val);
      chips.push({ columnId: col.id, columnLabel: col.label, value: val, label: opt?.label || val });
    });
  });

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "h-9 gap-1 rounded-[10px] bg-card",
              open && "ring-2 ring-primary/20",
              activeCount > 0 && "border-primary/50"
            )}
          >
            <SlidersHorizontal className="h-4 w-4" />
            Filters
            {activeCount > 0 && (
              <span className="ml-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-semibold">
                {activeCount}
              </span>
            )}
          </Button>
        </PopoverTrigger>

        {/* Portaled (Radix) so an open dropdown never extends the page's scroll height — the
            previous absolute-in-flow popup grew a short page past its bg-card panel, exposing a
            grey band below (AUT-398). */}
        <PopoverContent
          align="start"
          sideOffset={10}
          {...(pinBelow ? { side: "bottom" as const, avoidCollisions: false } : {})}
          className={cn(
            "w-auto border-border rounded-[10px] shadow-lg p-5",
            pinBelow && "max-h-[var(--radix-popover-content-available-height)] overflow-y-auto",
            className,
          )}
        >
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Filters</span>
              <div className="flex items-center gap-2">
                {activeCount > 0 && (
                  <button
                    onClick={clearAll}
                    className="text-xs text-primary hover:text-primary/80 font-semibold transition-colors"
                  >
                    Clear all
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <div className="flex gap-6">
              {columns.map(col => {
                const twoColumns = col.optionColumns === 2;
                return (
                <div key={col.id} className={twoColumns ? "w-[min(36rem,80vw)]" :"min-w-[120px]"}>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{col.label}</p>
                  <div
                    data-testid={`filter-options-${col.id}`}
                    className={twoColumns
                      ? "grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 max-h-[min(24rem,60vh)] overflow-y-auto"
                      : "space-y-1"}
                  >
                    {col.options.map(opt => {
                      const isChecked = (activeFilters[col.id] || []).includes(opt.value);
                      return (
                        <label
                          key={opt.value}
                          className={cn(
                            "flex gap-2 px-2 py-1.5 rounded-md cursor-pointer text-sm transition-colors",
                            twoColumns ? "items-start" : "items-center",
                            isChecked ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                          )}
                        >
                          <Checkbox
                            checked={isChecked}
                            onCheckedChange={() => toggleValue(col.id, opt.value)}
                            className={twoColumns ? "mt-0.5 shrink-0" : undefined}
                          />
                          {opt.imageUrl && (
                            <img src={opt.imageUrl} alt="" className="h-5 w-5 rounded-[3px] bg-white object-contain border border-border/50 shrink-0" />
                          )}
                          <span className={twoColumns ? "text-sm leading-5 min-w-0 flex-1" : "text-sm whitespace-nowrap"}>{opt.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Filter chips rendered inline after the dropdown */}
      {chips.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {chips.map(chip => (
            <span
              key={`${chip.columnId}-${chip.value}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium border border-primary/20"
            >
              {chip.label}
              <button
                onClick={() => removeFilter(chip.columnId, chip.value)}
                className="hover:bg-primary/20 rounded-full p-0.5 transition-colors"
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          ))}
          <button
            onClick={clearAll}
            className="text-xs text-primary hover:text-primary/80 font-semibold transition-colors ml-1"
          >
            Clear all
          </button>
        </div>
      )}
    </>
  );
}
